import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Checkbox,
  Chip,
  Typography,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from "@mui/material";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ListAltIcon from "@mui/icons-material/ListAlt";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import GlassCard from "../../../components/common/GlassCard";

function getPriorityColor(priority) {
  switch (priority) {
    case "critical":
      return "error.main";
    case "high":
      return "warning.main";
    case "medium":
      return "info.main";
    default:
      return "success.main";
  }
}

function getPriorityIcon(priority) {
  return (
    <FiberManualRecordIcon
      sx={{
        color: getPriorityColor(priority),
        fontSize: 14,
      }}
    />
  );
}

function CareTasksCard({
  tasks,
  onAddTask,
  onAnalyzeCareGaps,
  analyzeDisabled,
  onToggleComplete,
  onEditTask,
  onDeleteTask,
}) {
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [activeTaskId, setActiveTaskId] = useState(null);

  const handleMenuOpen = (event, taskId) => {
    setMenuAnchor(event.currentTarget);
    setActiveTaskId(taskId);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
    setActiveTaskId(null);
  };

  return (
    <GlassCard
      title="Care Tasks"
      addLabel="Add Care Task"
      icon={<ListAltIcon color="primary" />}
      onAdd={onAddTask}
      onAction={onAnalyzeCareGaps}
      actionLabel="Generate AI Tasks"
      actionIcon={<AutoAwesomeIcon />}
    >
      {tasks?.length ? (
        tasks.map((task) => (
          <Box
            key={task.id}
            mb={1.5}
            sx={{
              p: 1.5,
              border: "1px solid",
              borderColor: "divider",
              borderLeft: "4px solid",
              borderLeftColor: getPriorityColor(task.priority),
              borderRadius: 2,
              bgcolor: "background.paper",
              ...(task.ai_generated && {
                background:
                  "linear-gradient(135deg, rgba(138, 43, 226, 0.05) 0%, rgba(0, 191, 255, 0.05) 100%)",
                borderImage:
                  "linear-gradient(135deg, rgba(138, 43, 226, 0.3), rgba(0, 191, 255, 0.3)) 1",
              }),
            }}
          >
            <Box display="flex" alignItems="flex-start" gap={1}>
              <Checkbox
                checked={task.status === "completed"}
                onChange={() => onToggleComplete(task.id)}
                size="small"
                sx={{ mt: -0.5 }}
                aria-label={task.status === "completed" ? "Mark task incomplete" : "Mark task complete"}
                data-field="task-status"
                data-task-id={task.id}
              />
              <Box flex={1}>
                <Box display="flex" alignItems="center" gap={0.5} mb={0.5} flexWrap="wrap">
                  {getPriorityIcon(task.priority)}
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      textDecoration: task.status === "completed" ? "line-through" : "none",
                      color: task.status === "completed" ? "text.secondary" : "text.primary",
                      flex: 1,
                    }}
                  >
                    {task.task_description}
                  </Typography>
                  {task.ai_generated && (
                    <Chip
                      icon={<AutoAwesomeIcon />}
                      label="AI"
                      size="small"
                      variant="outlined"
                      sx={{
                        borderColor: "primary.main",
                        color: "primary.main",
                        background:
                          "linear-gradient(135deg, rgba(138, 43, 226, 0.1), rgba(0, 191, 255, 0.1))",
                      }}
                    />
                  )}
                  <Tooltip title="Options">
                    <IconButton size="small" onClick={(e) => handleMenuOpen(e, task.id)}
                      aria-label="Task options"
                      data-action="task-menu"
                      data-task-id={task.id}
                    >
                      <MoreVertIcon aria-label="Task options" fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  Due: {task.due_date || "Not set"}
                </Typography>
                {task.assigned_to && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    Assigned: {task.assigned_to}
                  </Typography>
                )}
                {task.ai_generated && task.ai_reasoning && (
                  <Typography
                    variant="caption"
                    color="info.main"
                    display="block"
                    sx={{ mt: 0.5, fontStyle: "italic" }}
                  >
                    AI: {task.ai_reasoning}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          No pending tasks
        </Typography>
      )}

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (onEditTask && activeTaskId) {
              onEditTask(activeTaskId);
            }
          }}
          aria-label="Edit task"
          data-action="edit-task"
          data-task-id={activeTaskId}
        >
          <EditIcon fontSize="small" sx={{ mr: 1 }} /> Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleMenuClose();
            if (onDeleteTask && activeTaskId) {
              onDeleteTask(activeTaskId);
            }
          }}
          aria-label="Delete task"
          data-action="delete-task"
          data-task-id={activeTaskId}
        >
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} /> Delete
        </MenuItem>
      </Menu>
    </GlassCard>
  );
}

CareTasksCard.propTypes = {
  tasks: PropTypes.array,
  onAddTask: PropTypes.func.isRequired,
  onAnalyzeCareGaps: PropTypes.func.isRequired,
  analyzeDisabled: PropTypes.bool,
  onToggleComplete: PropTypes.func.isRequired,
  onEditTask: PropTypes.func,
  onDeleteTask: PropTypes.func.isRequired,
};

CareTasksCard.defaultProps = {
  tasks: [],
  analyzeDisabled: false,
  onEditTask: null,
};

export default CareTasksCard;
